import type { ProductGroupMember } from '../../../../infrastructure/ProductGroupClient';
import { isGroupMember, isProductMember } from '../../../../infrastructure/ProductGroupClient';
import { collectAttributePath, type ProductGroupSchema } from './treeQueries';

export interface AttributeGroup {
  value: string;
  children: TreeChild[];
}

export interface ProductLeaf {
  sku: string;
  productName?: string;
}

export type TreeChild = AttributeGroup | ProductLeaf;

export interface ProductGroupTreeForm {
  attributes: string[];
  groups: AttributeGroup[];
}

export const isProductLeaf = (child: TreeChild): child is ProductLeaf => 'sku' in child;
export const isAttributeGroup = (child: TreeChild): child is AttributeGroup => 'children' in child;

export type SectionPath = number[];

export interface SkuPickerSelection {
  sku: string;
  productName?: string;
}

export type PickerTarget =
  | { kind: 'replace'; path: SectionPath; childIndex: number }
  | { kind: 'add'; path: SectionPath };

export function emptyTreeForm(): ProductGroupTreeForm {
  return {
    attributes: ['color'],
    groups: [{ value: '', children: [] }],
  };
}

export function newAttributeGroup(): AttributeGroup {
  return { value: '', children: [] };
}

export function newSectionForDepth(depth: number, totalAttributes: number): AttributeGroup {
  const childDepth = depth + 1;
  if (childDepth >= totalAttributes) {
    return { value: '', children: [] };
  }
  return { value: '', children: [newSectionForDepth(childDepth, totalAttributes)] };
}

export function newProductLeaf(): ProductLeaf {
  return { sku: '' };
}

export function schemaToTreeForm(schema: ProductGroupSchema): ProductGroupTreeForm {
  const attributes = collectAttributePath(schema);
  const groups = groupMembersByValue(schema.members);
  return { attributes, groups };
}

function groupMembersByValue(members: ProductGroupMember[]): AttributeGroup[] {
  const orderedMembers = [...members]
    .map((member, indexAmongSiblings) => ({ member, sortKey: member.order ?? indexAmongSiblings }))
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(({ member }) => member);

  const sectionInsertionOrder: string[] = [];
  const sectionByValue = new Map<string, AttributeGroup>();
  orderedMembers.forEach((member) => {
    if (!sectionByValue.has(member.value)) {
      sectionInsertionOrder.push(member.value);
      sectionByValue.set(member.value, { value: member.value, children: [] });
    }
    const section = sectionByValue.get(member.value)!;
    if (isProductMember(member)) {
      const leaf: ProductLeaf = { sku: member.sku };
      if (member.name) {
        leaf.productName = member.name;
      }
      section.children.push(leaf);
    } else if (isGroupMember(member)) {
      const subSections = groupMembersByValue(member.members);
      section.children.push(...subSections);
    }
  });
  return sectionInsertionOrder.map((value) => sectionByValue.get(value)!);
}

export function treeFormToSchema(form: ProductGroupTreeForm): ProductGroupSchema {
  const normalizedAttributes = form.attributes.map((attribute) => attribute.trim());
  const topAttribute = normalizedAttributes[0];
  if (!topAttribute) {
    throw new Error('At least one attribute is required.');
  }
  const blankSectionPaths = form.groups.flatMap((group, groupIndex) =>
    findBlankSectionPaths(group, `Section ${groupIndex + 1}`),
  );
  if (blankSectionPaths.length > 0) {
    throw new Error(
      `${blankSectionPaths.length} section(s) have an empty value: ${blankSectionPaths.join('; ')}. ` +
        `Fill in every section's value before saving.`,
    );
  }
  const cleanedGroups = form.groups
    .map((group) => cleanAttributeGroup(group))
    .filter((group): group is AttributeGroup => group !== null);
  if (cleanedGroups.length === 0) {
    throw new Error('Add at least one section with a value and a picked product.');
  }
  const restAttributes = normalizedAttributes.slice(1);
  const members = cleanedGroups.flatMap((group, groupIndex) =>
    attributeGroupToSchemaMembers(group, restAttributes, groupIndex),
  );
  if (members.length === 0) {
    throw new Error('Each section needs at least one product.');
  }
  return { memberAttribute: topAttribute, members };
}

function cleanAttributeGroup(group: AttributeGroup): AttributeGroup | null {
  const trimmedValue = group.value.trim();
  const cleanedChildren: TreeChild[] = [];
  group.children.forEach((child) => {
    if (isProductLeaf(child)) {
      if (child.sku.trim() !== '') {
        cleanedChildren.push({ sku: child.sku.trim() });
      }
      return;
    }
    const cleanedSub = cleanAttributeGroup(child);
    if (cleanedSub) {
      cleanedChildren.push(cleanedSub);
    }
  });
  if (cleanedChildren.length === 0) {
    return null;
  }
  if (trimmedValue === '') {
    return null;
  }
  return { value: trimmedValue, children: cleanedChildren };
}

function findBlankSectionPaths(group: AttributeGroup, breadcrumb: string): string[] {
  const trimmedValue = group.value.trim();
  const subgroupChildren = group.children.filter(isAttributeGroup);
  const leafChildren = group.children.filter(isProductLeaf);
  const hasMeaningfulContent =
    leafChildren.some((leaf) => leaf.sku.trim() !== '') ||
    subgroupChildren.some((subgroup) => groupHasAnyContent(subgroup));

  const blankPaths: string[] = [];
  if (trimmedValue === '' && hasMeaningfulContent) {
    blankPaths.push(breadcrumb);
  }
  subgroupChildren.forEach((subgroup, subgroupIndex) => {
    const childBreadcrumb = `${breadcrumb} > ${trimmedValue || `[${subgroupIndex + 1}]`}`;
    blankPaths.push(...findBlankSectionPaths(subgroup, childBreadcrumb));
  });
  return blankPaths;
}

function groupHasAnyContent(group: AttributeGroup): boolean {
  return group.children.some((child) => {
    if (isProductLeaf(child)) return child.sku.trim() !== '';
    return groupHasAnyContent(child);
  });
}

// One editor section → one schema member tagged with `group.value`. Subgroup children become
// nested members inside a single branch wrapper; leaf children become the member's sku. This
// preserves the unique-sibling-value invariant the facade validates.
function attributeGroupToSchemaMembers(
  group: AttributeGroup,
  deeperAttributes: string[],
  orderAmongSiblings: number,
): ProductGroupMember[] {
  const subgroupChildren = group.children.filter(isAttributeGroup);
  const leafChildren = group.children.filter(isProductLeaf);
  const [nextAttribute, ...remaining] = deeperAttributes;

  if (subgroupChildren.length > 0 && nextAttribute) {
    const nestedMembers = subgroupChildren.flatMap((subgroup, subgroupIndex) =>
      attributeGroupToSchemaMembers(subgroup, remaining, subgroupIndex),
    );
    if (nestedMembers.length === 0) {
      return [];
    }
    return [
      {
        value: group.value,
        memberAttribute: nextAttribute,
        members: nestedMembers,
        order: orderAmongSiblings,
      },
    ];
  }

  // No deeper attribute available. Flatten any reachable leaves (including grandchildren of
  // stale subgroups left over after removing an attribute) into direct leaf members.
  const reachableSkus: string[] = [
    ...leafChildren.map((leaf) => leaf.sku),
    ...subgroupChildren.flatMap((subgroup) =>
      subgroup.children.filter(isProductLeaf).map((grandchild) => grandchild.sku),
    ),
  ];
  return reachableSkus.map((sku) => ({
    value: group.value,
    sku,
    order: orderAmongSiblings,
  }));
}

export function countLeavesInForm(form: ProductGroupTreeForm): number {
  return form.groups.reduce((sum, group) => sum + countLeavesInGroup(group), 0);
}

function countLeavesInGroup(group: AttributeGroup): number {
  return group.children.reduce((sum, child) => {
    if (isProductLeaf(child)) {
      return sum + (child.sku.trim() === '' ? 0 : 1);
    }
    return sum + countLeavesInGroup(child);
  }, 0);
}
